import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { getMatchItemVisibleText } from '@/shared/quiz/matchPairDisplay';
import { alertDialog, confirmDialog } from '@/ui_v3';

import { hasDuplicateMatchTexts } from '../components/EditableQuizCard/Match/matchDuplicate';
import { editingQuizIdAtom } from '../components/focus/editModeAtom';
import { focusField } from '../components/focus/focusUtils';
import { useBuildFormContext } from '../form/schema';
import {
  AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE,
  BuildQuizListFormSchema,
  getFirstQuizValidationErrorPath,
  validateQuizList,
} from '../form/schema/BuildQuizListSchema';

export type QuizListValidationResult =
  | { isValid: true }
  | { isValid: false; reason: 'no_quiz' | 'match_duplicate_cancel' }
  | {
      isValid: false;
      reason: 'invalid_quiz';
      quizIndex: number;
      errorPath?: (string | number)[];
      errorCode?: string;
      errorMessage?: string;
    };

export const useValidateQuizList = () => {
  const setEditId = useSetAtom(editingQuizIdAtom);
  const { t } = useTranslation(['build']);

  const { getValues } = useBuildFormContext();

  const validate = async (): Promise<QuizListValidationResult> => {
    const formData = getValues();

    const { success: quizListSuccess, error: quizListError } =
      BuildQuizListFormSchema.safeParse(formData);

    //먼저 퀴즈가 비어있는지 확인
    const isQuizEmpty =
      !quizListSuccess &&
      quizListError.errors.some(
        error => error.message === AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE,
      );

    if (isQuizEmpty) {
      await alertDialog.openAsync({
        header: {
          title: t('build:build.alert.noQuiz'),
        },
      });
      return { isValid: false, reason: 'no_quiz' };
    }
    const { success, error } = validateQuizList(formData.quizList);

    if (!success) {
      await alertDialog.openAsync({
        header: {
          title: t('build:build.quiz.invalid'),
        },
      });

      const targetPath =
        getFirstQuizValidationErrorPath(error) ??
        error?.errors[0]?.path.join('.') ??
        '0';
      const targetIndex = Number(targetPath.split('.')[0]);

      const targetQuizId = formData.quizList[targetIndex]?.id;

      setEditId(targetQuizId);

      focusField(`quizList.${targetPath}`, {
        delay: 100,
        scroll: true,
        focus: true,
      });

      const targetError = error?.errors.find(({ path }) => {
        const errorPath = path.join('.');
        return targetPath === errorPath || targetPath === `${errorPath}.text`;
      });

      return {
        isValid: false,
        reason: 'invalid_quiz',
        quizIndex: targetIndex,
        errorPath: targetError?.path.slice(1),
        errorCode: targetError?.code,
        errorMessage: targetError?.message,
      };
    }

    // 저장 직전 최종 게이트: 편집 중 안내는 우회 가능하므로, 같은 열에 중복 보기 텍스트가
    // 남아 있으면 저장 전 한 번 확인을 받는다.
    const hasMatchDuplicate = formData.quizList.some(quiz => {
      if (quiz.type !== 'match') return false;
      const leftTexts = quiz.matchData.leftItems.map(getMatchItemVisibleText);
      const rightTexts = quiz.matchData.rightItems.map(getMatchItemVisibleText);
      return (
        hasDuplicateMatchTexts(leftTexts) || hasDuplicateMatchTexts(rightTexts)
      );
    });

    if (hasMatchDuplicate) {
      const confirmed = await confirmDialog.openAsync({
        header: { title: t('build:build.match.duplicate.title') },
        body: t('build:build.match.duplicateSave.body'),
        cancelText: t('build:build.match.duplicate.cancel'),
        confirmText: t('build:build.match.duplicateSave.confirm'),
        shouldCloseOnDimClick: false,
      });

      if (!confirmed)
        return { isValid: false, reason: 'match_duplicate_cancel' };
    }

    return { isValid: true };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- validate 는 호출 시점에 getValues() 로 최신 폼을 읽는다. t·setEditId 는 안정 참조라 getValues 만 의존한다
  return useCallback(validate, [getValues]);
};
